import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LoadingScreen } from "@/components/LoadingScreen";
import { StyleReport } from "@/components/StyleReport";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AnalysisView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const { data } = await api.get(`/analyses/${id}`);
        if (!active) return;
        setAnalysis(data);
        if (data.status === "verarbeitung") timer.current = setTimeout(fetch, 4000);
      } catch {
        if (active) setNotFound(true);
      }
    };
    fetch();
    return () => {
      active = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1400px] px-8 py-32 text-center" data-testid="analysis-not-found">
        <h1 className="font-display text-3xl font-light">Analyse nicht gefunden</h1>
        <Button onClick={() => navigate("/")} className="mt-8 rounded-sm">
          Zur Übersicht
        </Button>
      </div>
    );

  if (!analysis) return <LoadingScreen percent={5} label="Beratung wird geladen" />;

  if (analysis.status === "verarbeitung")
    return <LoadingScreen percent={analysis.progress_percent} label={analysis.progress_label} />;

  if (analysis.status === "fehler")
    return (
      <div className="mx-auto max-w-2xl px-8 py-32 text-center" data-testid="analysis-error">
        <p className="label-xs mb-5">Etwas ist schiefgelaufen</p>
        <h1 className="font-display text-3xl font-light mb-6">Die Beratung konnte nicht erstellt werden</h1>
        <p className="text-sm text-muted-foreground mb-10">
          Bitte prüfe die Aufnahmen und starte die Analyse erneut.
        </p>
        <Button onClick={() => navigate("/neue-analyse")} className="rounded-sm h-12 px-8">
          Neue Analyse starten
        </Button>
      </div>
    );

  return (
    <div className="bg-secondary/40 min-h-screen pb-24" data-testid="analysis-view">
      <div className="no-print sticky top-[140px] z-30 border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-8 lg:px-12 h-20 flex items-center justify-between gap-6">
          <button
            data-testid="back-to-archive"
            onClick={() => navigate("/")}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Übersicht
          </button>
          <div className="flex items-center gap-4">
            <Button
              data-testid="email-report-button"
              variant="outline"
              onClick={() => toast.info("E-Mail-Versand wird in Kürze freigeschaltet")}
              className="h-12 px-6 rounded-sm text-[11px] uppercase tracking-[0.2em] font-semibold border-border bg-white hover:border-foreground/40"
            >
              <EnvelopeSimple size={16} className="mr-3" /> Per E-Mail
            </Button>
            <Button
              data-testid="print-report-button"
              onClick={() => window.print()}
              className="h-12 px-8 rounded-sm bg-foreground text-primary-foreground text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-foreground/90 hover:-translate-y-[1px] transition-transform duration-200"
            >
              <Printer size={16} className="mr-3" /> PDF speichern
            </Button>
          </div>
        </div>
      </div>

      <div className="py-14 overflow-x-auto">
        <div className="origin-top scale-[0.62] sm:scale-[0.8] lg:scale-100 print:scale-100">
          <StyleReport analysis={analysis} />
        </div>
      </div>
    </div>
  );
}
